import React, { useState } from 'react'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { DeleteModal, Text } from '@components'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useCancelRequestedContacts } from '../../../hooks/api/mutations/useCancelRequestedContacts'
import type { ContactRequest } from '../../../hooks/api/queries/usePaginateContactRequests'

dayjs.extend(relativeTime)

const AVATAR_SIZE = 36

/**
 * The received requests no card of the list offers to answer (old requests
 * whose notification no longer exists).
 */
export const receivedWithoutCard = (
  received: ContactRequest[],
  cardRequestIds: Set<string>,
): ContactRequest[] => received.filter((request) => !cardRequestIds.has(request.id))

interface PendingRequestsFooterProps {
  /** Requests I sent that are still waiting: each can be cancelled. */
  sent: ContactRequest[]
  /** Requests I received that have no card: the name leads to the profile, where « Accepter » is. */
  received: ContactRequest[]
}

/**
 * Under the cards of the « Demandes » tab: the requests still waiting that
 * the cards do not show. Kept light: names and text actions only.
 */
export const PendingRequestsFooter = ({
  sent,
  received,
}: PendingRequestsFooterProps): React.ReactElement | null => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { t } = useLanguage()
  const navigation = useNavigation()
  const { mutate: cancelRequest } = useCancelRequestedContacts()
  const [requestToCancel, setRequestToCancel] = useState<ContactRequest | null>(null)

  if (sent.length === 0 && received.length === 0) return null

  const openProfile = (profileId: string) =>
    (navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId },
    })

  const renderRow = (
    request: ContactRequest,
    person: ContactRequest['recipient'],
    testID: string,
    action?: React.ReactElement,
  ) => (
    <View key={request.id} testID={testID} style={styles.row}>
      <Image
        source={person.avatarUrl ? { uri: person.avatarUrl } : defaultImage}
        style={styles.avatar}
      />
      <View style={styles.person}>
        <TouchableOpacity accessibilityRole="link" onPress={() => openProfile(person.id)}>
          <Text type="bodyBold" numberOfLines={1}>
            {person.fullName}
          </Text>
        </TouchableOpacity>
        <Text type="bodySmall" color={theme.colors.greyBlue}>
          {dayjs(request.createdAt).fromNow()}
        </Text>
      </View>
      {action}
    </View>
  )

  return (
    <View testID="pending-requests-footer" style={styles.container}>
      {sent.length > 0 && (
        <View>
          <Text type="bodySmallBold" color={theme.colors.greyBlue} style={styles.heading}>
            {t('notifications.sentRequests')}
          </Text>
          {sent.map((request) =>
            renderRow(
              request,
              request.recipient,
              `sent-request-${request.id}`,
              <TouchableOpacity
                testID={`sent-request-cancel-${request.id}`}
                accessibilityRole="button"
                style={styles.action}
                onPress={() => setRequestToCancel(request)}
              >
                <Text type="bodySmall" color={theme.colors.primary}>
                  {t('ghostProfile.cancelRequest')}
                </Text>
              </TouchableOpacity>,
            ),
          )}
        </View>
      )}
      {received.length > 0 && (
        <View>
          <Text type="bodySmallBold" color={theme.colors.greyBlue} style={styles.heading}>
            {t('notifications.receivedRequests')}
          </Text>
          {received.map((request) =>
            renderRow(request, request.sender, `received-request-${request.id}`),
          )}
        </View>
      )}
      <DeleteModal
        isVisible={requestToCancel !== null}
        setIsVisible={(isVisible) => {
          if (!isVisible) setRequestToCancel(null)
        }}
        title=""
        heading={t('ghostProfile.cancelRequestHeading')}
        description={t('ghostProfile.cancelRequestTitle', {
          name: requestToCancel?.recipient.fullName ?? '',
        })}
        deleteLabel={t('ghostProfile.cancelRequest')}
        cancelLabel={t('common.back')}
        onDelete={() => {
          if (requestToCancel) cancelRequest({ requestedContactId: requestToCancel.id })
        }}
      />
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      marginTop: theme.spacing.sm,
      gap: theme.spacing.lg,
    },
    heading: {
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: theme.spacing.sm,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.xs,
    },
    avatar: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      backgroundColor: theme.colors.grey10,
    },
    person: {
      flex: 1,
      alignItems: 'flex-start',
    },
    action: {
      paddingVertical: theme.spacing.xs,
    },
  })
